import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, FileDown, FolderOpen, Loader2, Save, Trash2, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";

/* Clientes corporativos que no entregan balance ni firman pagarés (compran con factura crédito): la línea se justifica con
   pruebas que la auditoría puede verificar. Manda el más bajo de: lo que ya demostró (deuda más alta que pagó bien), lo que
   nos compra en un año, el peso en nuestra cartera y su tamaño (si hay ventas públicas). */

const usd = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? "—" : "US$ " + Math.round(Number(n)).toLocaleString("es-PY"));
const aNum = (t) => {
  const s = String(t ?? "").replace(/\./g, "").replace(",", ".").replace(/[^\d.-]/g, "");
  return s === "" ? "" : Number(s);
};
const miles = (v) => (v === "" || v === null || v === undefined ? "" : Number(v).toLocaleString("es-PY"));
const fecha = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");

async function esperarTarea(job, maxSeg = 900) {
  for (let t = 0; t < maxSeg; t += 3) {
    await new Promise((ok) => setTimeout(ok, 3000));
    const st = (await api.get(`/analisis-cliente/tarea/${job}`, { timeout: 30000 })).data;
    if (st.estado === "error") throw Object.assign(new Error(st.error), { response: { data: { detail: st.error } } });
    if (st.estado === "listo") return st.resultado;
  }
  throw Object.assign(new Error("timeout"), { response: { data: { detail: "Tardó demasiado. Probá de nuevo." } } });
}

const DATOS_VACIO = {
  categoria: "B", en_construccion: false, fuente: "", fuente_link: "", calificacion: "", ventas_publicas: "",
  informconf: "", informconf_fecha: "", confirmacion_fecha: "", confirmacion_monto: "", facturas_conformadas: false,
  plazo_dias: "", linea_actual: "", linea_pedida: "", acta: "", acta_fecha: "",
};
const INFORMCONF = ["Sin registros negativos", "Atrasos menores", "Atrasos importantes", "Inhabilitado / morosidad"];
const TECHO_NOMBRE = { experiencia: "Lo que ya demostró", necesidad: "Lo que nos compra", concentracion: "Peso en nuestra cartera", tamano: "Tamaño del cliente" };

export default function AnalisisCorporativo() {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState({});
  const [cliente, setCliente] = useState("");
  const [codigo, setCodigo] = useState("");
  const [hist, setHist] = useState(null);       // {filas, resumen, archivo}
  const [datos, setDatos] = useState(DATOS_VACIO);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guardados, setGuardados] = useState([]);
  const [verReglas, setVerReglas] = useState(false);
  const histRef = useRef();

  useEffect(() => {
    api.get("/analisis-cliente/corporativo/config").then((r) => { setCfg(r.data.cfg); setCats(r.data.categorias || {}); }).catch(() => {});
    cargarGuardados();
  }, []);
  const cargarGuardados = () => api.get("/analisis-cliente/corporativo/guardados").then((r) => setGuardados(r.data.items || [])).catch(() => {});
  const set = (k, v) => { setDatos((d) => ({ ...d, [k]: v })); setRes(null); };
  const payload = () => ({ cliente, codigo, filas: hist?.filas || [], datos });

  const subirHistorial = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const j = (await api.post("/analisis-cliente/historial-compras", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
      const h = j.resultado || (await esperarTarea(j.job));
      setHist({ filas: h.filas, resumen: h.resumen, archivo: h.archivo });
      if (h.cliente && !cliente) setCliente(h.cliente);
      if (h.codigo && !codigo) setCodigo(h.codigo);
      setRes(null);
      toast.success(`Historial: ${h.resumen?.facturas || 0} facturas, ${h.resumen?.dias_prom ?? "—"} días de atraso promedio`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el historial");
    } finally { setBusy(false); if (histRef.current) histRef.current.value = ""; }
  };

  const calcular = async () => {
    setBusy(true);
    try { setRes((await api.post("/analisis-cliente/corporativo", payload(), { timeout: 120000 })).data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo calcular"); }
    finally { setBusy(false); }
  };
  const pdf = async () => {
    setBusy(true);
    try { window.open((await api.post("/analisis-cliente/corporativo/pdf", payload(), { timeout: 120000 })).data.url, "_blank"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el PDF"); }
    finally { setBusy(false); }
  };
  const guardar = async () => {
    setBusy(true);
    try { await api.post("/analisis-cliente/corporativo/guardar", payload(), { timeout: 120000 }); toast.success("Guardado: queda registrado quién lo hizo y cuándo"); cargarGuardados(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
    finally { setBusy(false); }
  };
  const abrir = async (id) => {
    try {
      const d = (await api.get(`/analisis-cliente/corporativo/guardados/${id}`)).data.payload;
      setCliente(d.cliente || ""); setCodigo(d.codigo || ""); setDatos({ ...DATOS_VACIO, ...(d.datos || {}) });
      setHist(d.filas?.length ? { filas: d.filas, resumen: null, archivo: "guardado" } : null);
      setRes((await api.post("/analisis-cliente/corporativo", d, { timeout: 120000 })).data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir"); }
  };
  const borrar = async (id) => {
    try { await api.delete(`/analisis-cliente/corporativo/guardados/${id}`); cargarGuardados(); } catch { toast.error("No se pudo borrar"); }
  };
  const guardarReglas = async () => {
    try { setCfg((await api.put("/analisis-cliente/corporativo/config", { cfg })).data.cfg); toast.success("Reglas guardadas"); setRes(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron guardar"); }
  };
  const copiar = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Texto copiado"); } catch { toast.error("No se pudo copiar: seleccionalo a mano"); }
  };

  const inp = "h-9 rounded border border-slate-200 bg-white px-2.5 text-sm";
  const numIn = (k, ph, w = "w-36") => <input value={miles(datos[k])} onChange={(e) => set(k, aNum(e.target.value))} inputMode="decimal" placeholder={ph} className={`${inp} ${w} text-right`} />;
  const h = res?.historial || hist?.resumen;

  return (
    <div className="space-y-4" data-testid="analisis-corporativo">
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Cliente corporativo: sin balance ni pagarés</h2>
          <p className="text-xs text-slate-500">Para empresas grandes que compran solo con factura crédito (multinacionales, cooperativas grandes, forestales). La línea se justifica con pruebas que la auditoría puede verificar; manda el límite más bajo.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={cliente} onChange={(e) => { setCliente(e.target.value); setRes(null); }} placeholder="Nombre del cliente" className={inp + " w-72"} data-testid="corp-cliente" />
          <input value={codigo} onChange={(e) => { setCodigo(e.target.value); setRes(null); }} placeholder="Código" className={inp + " w-28"} />
          <input ref={histRef} type="file" accept=".pdf,.xlsx,.xls,.csv,application/pdf" onChange={subirHistorial} className="hidden" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => histRef.current?.click()} className="gap-1.5" data-testid="corp-historial">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-[#0f5233]" />} Historial de pagos (PDF o Excel)
          </Button>
          {hist && <span className="text-xs text-slate-600">📒 {hist.resumen ? `${hist.resumen.facturas} facturas desde ${String(hist.resumen.desde || "").slice(0, 4)} · ${hist.resumen.dias_prom} días promedio` : `${hist.filas.length} facturas (guardado)`}</span>}
        </div>

        <div className="grid gap-4 lg:grid-cols-3 text-xs text-slate-700">
          <div className="space-y-2">
            <div className="font-semibold text-slate-800">1. ¿Qué respaldo externo tiene?</div>
            {["A", "B", "C"].map((k) => (
              <label key={k} className={`flex gap-2 rounded border p-2 ${datos.categoria === k ? "border-[#0f5233] bg-emerald-50" : "border-slate-200"}`}>
                <input type="radio" name="corp-cat" checked={datos.categoria === k} onChange={() => set("categoria", k)} />
                <span><b>{k} · {cats[k]?.nombre || ""}</b> <span className="text-slate-500">({cats[k]?.factor}% del pico pagado)</span><br /><span className="text-slate-500">{cats[k]?.ejemplos}</span></span>
              </label>
            ))}
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!datos.en_construccion} onChange={(e) => set("en_construccion", e.target.checked)} /> Proyecto en construcción (todavía no genera ingresos propios)</label>
          </div>
          <div className="space-y-2">
            <div className="font-semibold text-slate-800">2. Información y pruebas</div>
            <input value={datos.fuente} onChange={(e) => set("fuente", e.target.value)} placeholder="Fuente pública (ej. balance de la matriz, memoria de asamblea)" className={inp + " w-full"} />
            <input value={datos.fuente_link} onChange={(e) => set("fuente_link", e.target.value)} placeholder="Link o referencia" className={inp + " w-full"} />
            <div className="flex flex-wrap items-center gap-1.5">
              <input value={datos.calificacion} onChange={(e) => set("calificacion", e.target.value)} placeholder="Calificación (ej. AA-py)" className={inp + " w-36"} />
              US$ {numIn("ventas_publicas", "ventas anuales", "w-32")}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <select value={datos.informconf} onChange={(e) => set("informconf", e.target.value)} className={inp}>
                <option value="">Informconf…</option>{INFORMCONF.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input type="date" value={datos.informconf_fecha} onChange={(e) => set("informconf_fecha", e.target.value)} className={inp} title="Fecha de la consulta" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              Confirmación de saldo <input type="date" value={datos.confirmacion_fecha} onChange={(e) => set("confirmacion_fecha", e.target.value)} className={inp} />
              US$ {numIn("confirmacion_monto", "monto", "w-28")}
            </div>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!datos.facturas_conformadas} onChange={(e) => set("facturas_conformadas", e.target.checked)} /> Guardamos facturas con firma de recibido y orden de compra</label>
          </div>
          <div className="space-y-2">
            <div className="font-semibold text-slate-800">3. Línea</div>
            <div className="flex items-center gap-1.5">US$ {numIn("linea_actual", "0")} línea que tiene hoy</div>
            <div className="flex items-center gap-1.5">US$ {numIn("linea_pedida", "0")} línea que se pide</div>
            <div className="flex items-center gap-1.5">{numIn("plazo_dias", "auto", "w-20")} días de plazo <span className="text-slate-400">(vacío = el del historial)</span></div>
            <div className="font-semibold text-slate-800 pt-1">Aprobación del directorio</div>
            <div className="flex flex-wrap items-center gap-1.5">
              <input value={datos.acta} onChange={(e) => set("acta", e.target.value)} placeholder="Acta Nº" className={inp + " w-32"} />
              <input type="date" value={datos.acta_fecha} onChange={(e) => set("acta_fecha", e.target.value)} className={inp} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy} onClick={calcular} className="gap-1.5 bg-[#0f5233] hover:bg-[#0c3f27]" data-testid="corp-calcular">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Calcular límite
          </Button>
          <button type="button" onClick={() => setVerReglas(!verReglas)} className="text-xs text-slate-500 underline">Reglas</button>
        </div>
        {verReglas && cfg && (
          <div className="flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
            {[["conc_pct", "% máximo de toda nuestra cartera en un cliente"], ["ventas_pct", "% máximo de sus ventas anuales"], ["min_anios", "años mínimos de relación"],
              ["confirmacion_meses", "meses de validez de la confirmación de saldo"], ["informconf_meses", "meses de validez de Informconf"]].map(([k, t]) => (
              <label key={k} className="flex items-center gap-1"><input value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className={inp + " w-16 text-right"} /> {t}</label>
            ))}
            <Button size="sm" variant="outline" onClick={guardarReglas}>Guardar reglas</Button>
          </div>
        )}
      </div>

      {res && (
        <div className="space-y-3" data-testid="corp-resultado">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy} onClick={pdf} className="gap-1.5 bg-[#0f5233] hover:bg-[#0c3f27]"><FileDown className="h-4 w-4" /> Informe para auditoría (PDF)</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={guardar} className="gap-1.5"><Save className="h-4 w-4 text-[#0f5233]" /> Guardar</Button>
          </div>
          {res.bloqueos.length > 0 ? (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
              <div className="text-lg font-extrabold text-red-700">No corresponde línea sin garantías</div>
              <ul className="mt-1 list-disc pl-4 text-sm text-red-800">{res.bloqueos.map((b) => <li key={b}>{b}</li>)}</ul>
              <p className="mt-1 text-xs text-red-700">Se analiza como un cliente normal: con balance y garantías.</p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-4 rounded-lg border-2 border-[#0f5233] bg-emerald-50 p-4">
              <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Línea justificada</div><div className="text-3xl font-extrabold text-[#0f5233]">{usd(res.linea)}</div></div>
              <div className="flex-1 min-w-[240px] text-sm text-slate-700">
                Categoría <b>{res.categoria}</b> ({res.categoria_info.nombre.toLowerCase()}). Lo pone <b>{(TECHO_NOMBRE[res.manda] || "").toLowerCase()}</b>.
                Revisión antes del {fecha(res.revision)}.
              </div>
            </div>
          )}
          {res.msg_pedida && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{res.msg_pedida}</div>}
          {res.avisos.length > 0 && <ul className="list-disc pl-4 text-sm text-amber-800">{res.avisos.map((a) => <li key={a}>{a}</li>)}</ul>}

          <div className="grid gap-2 md:grid-cols-4">
            {res.techos.map((t) => (
              <div key={t.k} className={"rounded-md border p-2.5 " + (t.k === res.manda ? "border-[#0f5233] bg-emerald-50" : "border-slate-200 bg-white")}>
                <div className="text-[11px] text-slate-500">{t.nombre}{t.k === res.manda ? " · manda este" : ""}</div>
                <div className="text-lg font-bold text-[#0f5233]">{usd(t.monto)}</div>
                <div className="text-[11px] text-slate-500">{t.como}</div>
              </div>
            ))}
          </div>

          {h && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
              <b>Cómo nos pagó:</b> {h.facturas} facturas desde {String(h.desde || "").slice(0, 4)}, relación continua de {String(h.anios_cliente).replace(".", ",")} años,
              atraso promedio {h.dias_prom} días (máximo {h.dias_max}), {String(h.patron || "").toLowerCase()}.
              {res.picos && <> Deuda más alta que pagó bien: <b>{usd(res.picos.pico_bien.monto)}</b> el {fecha(res.picos.pico_bien.fecha)}.</>}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold text-[#0f5233] mb-2">Pruebas para auditoría · {res.evidencia_ok} de {res.evidencia.length}</div>
            <ul className="space-y-1.5">
              {res.evidencia.map((e) => (
                <li key={e.item} className="flex gap-2 text-sm">
                  {e.ok ? <CheckCircle2 className="h-4 w-4 flex-none text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 flex-none text-red-500 mt-0.5" />}
                  <span><b>{e.item}:</b> <span className="text-slate-600">{e.detalle}</span></span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-xs font-semibold text-[#0f5233] mb-1">Alarmas: la línea se congela si pasa cualquiera</div>
              <ul className="list-disc pl-4 text-sm text-slate-700 space-y-0.5">{res.alarmas.map((a) => <li key={a}>{a}</li>)}</ul>
            </div>
            {res.excepcion && (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#0f5233] mb-1">Texto de la excepción para el directorio
                  <button type="button" onClick={() => copiar(res.excepcion)} className="ml-auto flex items-center gap-1 text-slate-500 hover:text-slate-800"><Copy className="h-3.5 w-3.5" /> Copiar</button>
                </div>
                <p className="text-sm text-slate-700 select-all">{res.excepcion}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {guardados.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold text-slate-700 mb-2">Análisis corporativos guardados</div>
          <ul className="divide-y divide-slate-100 text-sm">
            {guardados.slice(0, 30).map((g) => (
              <li key={g.id} className="flex flex-wrap items-center gap-2 py-1.5">
                <b className="text-slate-800">{g.cliente}</b>
                <span className="text-xs text-slate-500">cat. {g.categoria} · {g.bloqueado ? "sin línea" : usd(g.linea)} · pruebas {g.evidencia} · {fecha(g.created_at)} · {g.created_by_name}</span>
                <button type="button" onClick={() => abrir(g.id)} className="ml-auto text-slate-500 hover:text-[#0f5233]" title="Abrir"><FolderOpen className="h-4 w-4" /></button>
                <button type="button" onClick={() => borrar(g.id)} className="text-slate-400 hover:text-red-600" title="Borrar"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
